// src/data/schools.ts
// -------------------
// Repository for the `schools` reference table — backs the school-name
// autocomplete (filtered by state). Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

// `id` is the schools.id surrogate (bigint). It's carried so the picker can emit the
// chosen school's id (BYOD, db-198/db-199) — a free-typed / chip / "Other" choice has none.
export type SchoolRow = {
  id: number | null;
  name: string | null;
  suburb: string | null;
  school_type: string | null;
};

// Up to 8 schools whose name matches `q`, name-ordered, optionally scoped to a
// state. `contains=false` → "q%" prefix match; `contains=true` → "%q%" fallback.
// Returns [] on error / no client. (The state filter is applied before
// order/limit so the typed filter-builder chain stays valid.)
export async function searchSchools(
  q: string,
  state?: string | null,
  contains = false,
  // Fetch headroom for the autocomplete: it shows the first 10 and offers a
  // "Show more" to reveal the rest of this batch (John, 31 Jul 2026).
  limit = 30,
  // Year-level phase filter (John, 9 Aug 2026): a school_type to EXCLUDE — "Secondary"
  // for a primary year level, "Primary" for a secondary one. Applied leniently so a
  // Combined- or blank-type school is still shown (only the opposite phase is hidden).
  excludeType?: string | null
): Promise<SchoolRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  const pattern = contains ? `%${q}%` : `${q}%`;
  // Exclude soft-deleted schools (db-56) so a deactivated school never appears
  // in the wizard autocomplete.
  let filter = c
    .from("schools")
    .select("id, name, suburb, school_type")
    .eq("deactivated", false)
    .ilike("name", pattern);
  if (state) filter = filter.eq("state", state);
  // Phase filter: hide the opposite phase but keep NULL-type + Combined schools.
  if (excludeType) filter = filter.or(`school_type.is.null,school_type.neq.${excludeType}`);
  const { data, error } = await filter.order("name").limit(limit);
  if (error) {
    logger.warn("searchSchools failed:", error.message);
    return [];
  }
  return (data ?? []) as unknown as SchoolRow[];
}

// A single school's canonical name / suburb / state by id — used to give the BYOD
// lookup clean inputs (the child stores a display name that may carry a "— Suburb"
// suffix). null on error / no client / not found.
export async function getSchoolById(
  id: number
): Promise<{ id: number; name: string; suburb: string | null; state: string | null } | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("schools")
    .select("id, name, suburb, state")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getSchoolById failed:", error.message);
    return null;
  }
  return (data ?? null) as {
    id: number;
    name: string;
    suburb: string | null;
    state: string | null;
  } | null;
}

// Which of the given school names exist as ACTIVE schools — powers the "Other"
// alert (Option B, John 9 Aug 2026): at submit we look up each confirmed non-chip
// school name; any NOT returned here is a free-typed ("Other") school and triggers
// the alert. Names are matched exactly (the caller strips the picker's "— Suburb"
// suffix first). Small list (a few per request). [] on error / no client.
export async function listExistingSchoolNames(
  names: string[],
  state?: string | null
): Promise<string[]> {
  const c = getTypedClient();
  if (!c || names.length === 0) return [];
  let q = c.from("schools").select("name").eq("deactivated", false).in("name", names);
  if (state) q = q.eq("state", state);
  const { data, error } = await q.limit(200);
  if (error) {
    logger.warn("listExistingSchoolNames failed:", error.message);
    return [];
  }
  return (data ?? []).map((r) => r.name).filter(Boolean) as string[];
}

// ── Admin console (Manage → Schools) ─────────────────────────────────────────
// The full school row the admin list + edit modal + CSV import work with. `id`
// is the surrogate key updates go through; `acara_id` is the CSV dedup key.

export interface AdminSchoolRow {
  id: number;
  acara_id: string | null;
  name: string;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  school_type: string | null;
  school_sl: boolean;
  deactivated: boolean;
}

// Typed as `string` (not a literal) so supabase-js doesn't deep-parse the select.
const ADMIN_SCHOOL_COLS: string =
  "id, acara_id, name, suburb, state, postcode, school_type, school_sl, deactivated";

// Default (and admin-screen) page size. IMPORTANT: PostgREST silently caps an
// un-limited select at 1,000 rows — there are ~10,900 schools, so a
// fetch-everything call truncates. Filtering is therefore SERVER-side with an
// explicit limit (same rule as suppliers, docs/uniform-authorisation.md).
export const SCHOOLS_LIST_LIMIT = 200;

// The `.in()` batch size for the ACARA dedup lookup (a full ACARA export is
// ~10,900 codes, well over any single-request URL/row ceiling).
const ACARA_LOOKUP_CHUNK = 200;

export interface ListSchoolsOptions {
  // Case-insensitive substring match on name OR suburb OR acara_id.
  search?: string;
  // Exact state code (e.g. "QLD").
  state?: string;
  // Page size (defaults to SCHOOLS_LIST_LIMIT).
  limit?: number;
  // 0-based page index; the offset is page * limit.
  page?: number;
}

export interface SchoolsPage {
  rows: AdminSchoolRow[];
  // Total matching rows across ALL pages (for the pager) — the filtered count,
  // not just this page.
  total: number;
}

// List a PAGE of schools for the admin console, name-ordered, filters applied
// SERVER-side. Paginated with .range() (there are ~10,900 schools; PostgREST caps
// an un-limited select at 1,000). `count: "exact"` returns the filtered total so
// the screen can render page links. Deactivated rows are NOT filtered here — the
// admin console shows them (badge / reactivate toggle). Returns null on error / no
// client.
export async function listSchools(opts: ListSchoolsOptions = {}): Promise<SchoolsPage | null> {
  const c = getTypedClient();
  if (!c) return null;
  const limit = opts.limit ?? SCHOOLS_LIST_LIMIT;
  const from = (opts.page ?? 0) * limit;
  const to = from + limit - 1;
  let q = c.from("schools").select(ADMIN_SCHOOL_COLS, { count: "exact" });
  if (opts.state) q = q.eq("state", opts.state);
  // Strip PostgREST .or() syntax characters from the user's search text so a
  // stray comma/paren/percent can't break (or inject into) the filter expression.
  const s = (opts.search ?? "")
    .replace(/[,()%]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (s) q = q.or(`name.ilike.%${s}%,suburb.ilike.%${s}%,acara_id.ilike.%${s}%`);
  const { data, error, count } = await q.order("name").range(from, to);
  if (error) {
    logger.warn("listSchools failed:", error.message);
    return null;
  }
  return { rows: (data as unknown as AdminSchoolRow[]) ?? [], total: count ?? 0 };
}

type SchoolPatch = Database["public"]["Tables"]["schools"]["Update"];
type SchoolInsert = Database["public"]["Tables"]["schools"]["Insert"];
type WriteResult = { error: { message: string } | null };

// Update a school by surrogate id (the admin edit modal + deactivate/reactivate
// toggle). Takes a loose patch object (admin form state) cast to the generated
// Update shape, per the repo write convention. Logs on error and returns
// { error }; a missing client returns a null error (caller won't crash).
export async function updateSchoolById(
  id: number | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("schools")
    .update(patch as SchoolPatch)
    .eq("id", id ?? 0);
  if (error) logger.warn("updateSchoolById failed:", error.message);
  return { error };
}

// Create one or more school rows (the admin "+ New school" form, and the CSV
// import which inserts the ticked new rows in chunks). Takes loose objects cast
// to the generated Insert shape, per the repo write convention. Logs on error
// and returns { error }.
export async function createSchools(rows: object[]): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (rows.length === 0) return { error: null };
  const { error } = await c.from("schools").insert(rows as SchoolInsert[]);
  if (error) logger.warn("createSchools failed:", error.message);
  return { error };
}

// Which of the given ACARA IDs already exist in `schools` (INCLUDING deactivated
// rows — a soft-deleted school still counts as "already present", so a re-import
// never resurrects it). Batched `.in()` with an explicit per-chunk limit.
// Returns the set of existing acara_ids as a plain array, or null on error.
export async function getExistingAcaraIds(acaraIds: string[]): Promise<string[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const ids = Array.from(new Set(acaraIds.filter(Boolean)));
  const found: string[] = [];
  for (let i = 0; i < ids.length; i += ACARA_LOOKUP_CHUNK) {
    const chunk = ids.slice(i, i + ACARA_LOOKUP_CHUNK);
    const { data, error } = await c
      .from("schools")
      .select("acara_id")
      .in("acara_id", chunk)
      .limit(chunk.length);
    if (error) {
      logger.warn("getExistingAcaraIds chunk failed:", error.message);
      return null;
    }
    for (const r of (data as unknown as Array<{ acara_id: string | null }>) ?? []) {
      if (r.acara_id) found.push(r.acara_id);
    }
  }
  return found;
}
